import { useState } from 'react';

import { ModalBase } from '../../../components/modals/ModalBase';

import type { InsightActionType } from '../../../types';
import type React from 'react';

// D5 — actionType é enum de 5. `create_pendency_*` e `coach_schedule` são REAIS
// (criam recurso e retornam linkedResourceId); `message_team`/`schedule_meeting`
// seguem stubs v1 (no-op) → rotulados honesto, sem prometer efeito.
// `coach_schedule` só está disponível quando o insight tem atendente-alvo
// (targetUserId) — coaching é sempre sobre um atendente específico.
interface ActionOpt {
  value: InsightActionType;
  label: string;
  real: boolean;
}

const ACTION_OPTS: ActionOpt[] = [
  { value: 'create_pendency_self', label: 'Criar pendência para mim', real: true },
  { value: 'create_pendency_manager', label: 'Criar pendência para o gestor', real: true },
  { value: 'coach_schedule', label: 'Agendar coaching', real: true },
  { value: 'message_team', label: 'Mensagem ao time', real: false },
  { value: 'schedule_meeting', label: 'Agendar reunião', real: false },
];

const AGENDA_MIN = 10;

export interface TakeActionModalProps {
  open: boolean;
  isPending: boolean;
  failed: boolean;
  outcome: string | null;
  /** Atendente-alvo do insight (habilita `coach_schedule`). Null = sem alvo. */
  targetUserId?: string | null;
  /** Nome do atendente-alvo, para exibição. */
  targetName?: string | null;
  onClose: () => void;
  onConfirm: (args: { actionType: InsightActionType; payload: Record<string, unknown> }) => void;
}

export function TakeActionModal({
  open,
  isPending,
  failed,
  outcome,
  targetUserId = null,
  targetName = null,
  onClose,
  onConfirm,
}: TakeActionModalProps): React.JSX.Element | null {
  const [actionType, setActionType] = useState<InsightActionType>('create_pendency_self');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [scheduledFor, setScheduledFor] = useState('');
  const [agenda, setAgenda] = useState('');
  if (!open) return null;

  const hasTarget = targetUserId !== null;
  const isCoach = actionType === 'coach_schedule';
  const isStub = ACTION_OPTS.find((o) => o.value === actionType)?.real === false;

  // Validade do coaching: precisa de data/hora válida + pauta com conteúdo mínimo.
  const scheduledIso = ((): string | null => {
    if (scheduledFor.trim().length === 0) return null;
    const d = new Date(scheduledFor);
    return Number.isNaN(d.getTime()) ? null : d.toISOString();
  })();
  const coachInvalid = isCoach && (scheduledIso === null || agenda.trim().length < AGENDA_MIN);

  const submit = (): void => {
    if (isCoach) {
      onConfirm({ actionType, payload: { scheduledFor: scheduledIso, agenda: agenda.trim() } });
      return;
    }
    const payload: Record<string, unknown> = {};
    if (title.trim().length > 0) payload.title = title.trim();
    if (description.trim().length > 0) payload.description = description.trim();
    onConfirm({ actionType, payload });
  };

  return (
    <ModalBase
      open={open}
      onClose={onClose}
      title="Tomar ação sobre o insight"
      width={540}
      footer={
        <div style={{ display: 'flex', gap: 8, marginLeft: 'auto' }}>
          <button className="btn-ghost-mini" type="button" onClick={onClose} disabled={isPending}>
            Cancelar
          </button>
          <button
            className="btn-ghost-mini btn-ghost-mini--primary"
            type="button"
            disabled={isPending || coachInvalid}
            onClick={submit}
          >
            {isPending ? 'Executando…' : 'Executar ação'}
          </button>
        </div>
      }
    >
      <div className="modal-form">
        <label className="modal-form__field">
          <span className="modal-form__label">Tipo de ação</span>
          <select
            className="modal-form__select"
            value={actionType}
            onChange={(e) => setActionType(e.target.value as InsightActionType)}
          >
            {ACTION_OPTS.map((o) => {
              const coachDisabled = o.value === 'coach_schedule' && !hasTarget;
              return (
                <option key={o.value} value={o.value} disabled={coachDisabled}>
                  {o.label}
                  {o.real ? '' : ' (em breve)'}
                  {coachDisabled ? ' (requer atendente-alvo)' : ''}
                </option>
              );
            })}
          </select>
        </label>

        {isStub && (
          <div
            className="modal-form__callout modal-form__callout--warn"
            style={{ fontSize: 'var(--text-xs)' }}
          >
            ⚠ Esta ação ainda não tem efeito nesta versão — registra a intenção, mas não cria nenhum
            recurso. As pendências (para mim / para o gestor) e o coaching são as ações funcionais
            hoje.
          </div>
        )}

        {isCoach ? (
          <>
            <div className="modal-form__callout" style={{ fontSize: 'var(--text-xs)' }}>
              Agenda um coaching para <strong>{targetName ?? 'o atendente do insight'}</strong>. Ele
              será notificado com a data e a pauta.
            </div>
            <label className="modal-form__field">
              <span className="modal-form__label">Data e hora</span>
              <input
                className="modal-form__input"
                type="datetime-local"
                value={scheduledFor}
                onChange={(e) => setScheduledFor(e.target.value)}
              />
            </label>
            <label className="modal-form__field">
              <span className="modal-form__label">Pauta (mín. {AGENDA_MIN} caracteres)</span>
              <textarea
                className="modal-form__textarea"
                value={agenda}
                onChange={(e) => setAgenda(e.target.value)}
                rows={3}
                placeholder="Ex.: Revisar abordagem em conversas sensíveis e prática de empatia."
              />
            </label>
          </>
        ) : (
          <>
            <label className="modal-form__field">
              <span className="modal-form__label">Título (opcional)</span>
              <input
                className="modal-form__input"
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Ex.: Coaching focal com a equipe do pico"
              />
            </label>
            <label className="modal-form__field">
              <span className="modal-form__label">Descrição (opcional)</span>
              <textarea
                className="modal-form__textarea"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
                placeholder="Contexto da ação derivado do insight…"
              />
            </label>
          </>
        )}

        {failed && (
          <div className="banner banner--danger" role="alert">
            <div className="banner__body">
              <span className="banner__title">Não foi possível registrar a ação.</span>
            </div>
          </div>
        )}
        {outcome !== null && (
          <div className="modal-form__callout" role="status">
            {outcome}
          </div>
        )}
      </div>
    </ModalBase>
  );
}
